import React, { useState, useEffect } from 'react';
import { Pill, Paperclip, Camera, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { healthService } from '@/services/healthService';
import { arrivalService } from '@/services/arrivalService';
import { farmService } from '@/services/farmService';
import { cycleService } from '@/services/cycleService';
import {
  MedicineProductRecord,
  SupplierRecord,
  FarmRecord,
  ProductionCycleRecord,
  TransactionCategoryRecord,
} from '@/types/database';

interface MedicineReceiptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (receipt: any) => void;
  cycleId?: string | null;
  farmId?: string | null;
}

export const MedicineReceiptModal: React.FC<MedicineReceiptModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  cycleId,
  farmId,
}) => {
  const [selectedSupplierId, setSelectedSupplierId] = useState<string>('');
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [selectedCycleId, setSelectedCycleId] = useState<string>(cycleId || '');
  const [selectedFarmId, setSelectedFarmId] = useState<string>(farmId || '');

  const [receiptDate, setReceiptDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [quantity, setQuantity] = useState<number | ''>(1);
  const [unitMeasurement, setUnitMeasurement] = useState<string>('vial');
  const [unitPrice, setUnitPrice] = useState<number | ''>('');
  const [invoiceNo, setInvoiceNo] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [attachedFile, setAttachedFile] = useState<File | null>(null);

  const [products, setProducts] = useState<MedicineProductRecord[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierRecord[]>([]);
  const [farms, setFarms] = useState<FarmRecord[]>([]);
  const [cycles, setCycles] = useState<ProductionCycleRecord[]>([]);
  const [categories, setCategories] = useState<TransactionCategoryRecord[]>([]);

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (cycleId) setSelectedCycleId(cycleId);
    if (farmId) setSelectedFarmId(farmId);
  }, [cycleId, farmId]);

  useEffect(() => {
    if (!isOpen) return;
    setErrorMsg(null);

    Promise.all([
      healthService.getMedicineProducts(),
      healthService.getMedicineTransactionCategories(),
      arrivalService.getSuppliers(),
      farmService.getActiveFarms(),
      cycleService.getCycles({ status: 'active' }),
    ]).then(([prods, cats, supps, fms, cycs]) => {
      setProducts(prods);
      setCategories(cats);
      setSuppliers(supps);
      setFarms(fms);
      setCycles(cycs);

      if (prods.length > 0 && !selectedProductId) {
        setSelectedProductId(prods[0].id);
        if (prods[0].default_unit_price) {
          setUnitPrice(Number(prods[0].default_unit_price));
        }
        if (prods[0].unit_measurement) {
          setUnitMeasurement(prods[0].unit_measurement);
        }
      }
      if (cats.length > 0 && !selectedCategoryId) {
        // Find default Meds category if present (e.g. 3030 or first medicine category)
        const defaultCat = cats.find((c) => c.code === '3030') || cats[0];
        setSelectedCategoryId(defaultCat.id);
      }
      if (supps.length > 0 && !selectedSupplierId) {
        setSelectedSupplierId(supps[0].id);
      }
      if (fms.length > 0 && !selectedFarmId && !farmId) {
        setSelectedFarmId(fms[0].id);
      }
    });
  }, [isOpen]);

  const handleProductChange = (productId: string) => {
    setSelectedProductId(productId);
    const prod = products.find((p) => p.id === productId);
    if (prod?.default_unit_price) {
      setUnitPrice(Number(prod.default_unit_price));
    }
    if (prod?.unit_measurement) {
      setUnitMeasurement(prod.unit_measurement);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setAttachedFile(e.target.files[0]);
    }
  };

  const selectedCategory = categories.find((c) => c.id === selectedCategoryId);
  const totalAmount = (Number(quantity) || 0) * (Number(unitPrice) || 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    if (!selectedSupplierId) {
      setErrorMsg('Please select a Supplier.');
      return;
    }
    if (!selectedProductId) {
      setErrorMsg('Please select a Medicine Product.');
      return;
    }
    if (!receiptDate) {
      setErrorMsg('Receipt Date is required.');
      return;
    }
    if (!quantity || Number(quantity) === 0) {
      setErrorMsg('Quantity cannot be zero.');
      return;
    }
    if (unitPrice === '' || Number(unitPrice) < 0) {
      setErrorMsg('Unit price must be 0 or positive.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        supplier_id: selectedSupplierId,
        product_id: selectedProductId,
        cycle_id: selectedCycleId || null,
        farm_id: selectedFarmId || null,
        receipt_date: receiptDate,
        quantity: Number(quantity),
        unit_measurement: unitMeasurement || 'vial',
        unit_price: Number(unitPrice),
        invoice_no: invoiceNo.trim() || null,
        notes: notes.trim() || null,
        category_id: selectedCategoryId || null,
        account_code: selectedCategory?.code || null,
      };

      const result = await healthService.createMedicineReceipt(payload, attachedFile);
      onSuccess(result);
      onClose();
    } catch (err: any) {
      console.error('Error recording medicine receipt:', err);
      setErrorMsg(err.message || 'Failed to record medicine receipt.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] flex flex-col p-4 sm:p-6 overflow-hidden">
        <DialogHeader className="pb-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-teal-100 text-teal-800 flex items-center justify-center">
              <Pill className="w-4 h-4" />
            </div>
            <div>
              <DialogTitle className="text-base sm:text-lg font-bold text-slate-900">
                Record Veterinary Medicine Receipt
              </DialogTitle>
              <p className="text-xs text-slate-500">
                Record medicine, vaccine, or vitamin purchase from supplier (Chart of Accounts 3030–3032).
              </p>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto py-3 space-y-4 no-scrollbar">
          {errorMsg && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Supplier & Receipt Date */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Supplier *</label>
              <select
                value={selectedSupplierId}
                onChange={(e) => setSelectedSupplierId(e.target.value)}
                className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-teal-600"
                required
              >
                <option value="">-- Select Supplier --</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.supplier_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Receipt Date *</label>
              <Input
                type="date"
                value={receiptDate}
                onChange={(e) => setReceiptDate(e.target.value)}
                className="h-9 text-xs"
                required
              />
            </div>
          </div>

          {/* Chart of Accounts Category & Medicine Product */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">COA Account Category *</label>
              <select
                value={selectedCategoryId}
                onChange={(e) => setSelectedCategoryId(e.target.value)}
                className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-teal-600"
                required
              >
                <option value="">-- Select COA Category --</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    [{c.code}] {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Medicine / Product *</label>
              <select
                value={selectedProductId}
                onChange={(e) => handleProductChange(e.target.value)}
                className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-teal-600"
                required
              >
                <option value="">-- Select Medicine Product --</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.product_name} {p.category ? `(${p.category})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Allocation: Destination Cycle or Farm Location */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Bound Cycle (Optional)</label>
              <select
                value={selectedCycleId}
                onChange={(e) => setSelectedCycleId(e.target.value)}
                className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs font-mono bg-white focus:outline-none focus:ring-2 focus:ring-teal-600"
              >
                <option value="">-- General / Farm Inventory --</option>
                {cycles.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.cycle_code} ({c.growers?.full_name || 'Company-Owned'})
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Farm Location (Optional)</label>
              <select
                value={selectedFarmId}
                onChange={(e) => setSelectedFarmId(e.target.value)}
                className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-teal-600"
              >
                <option value="">-- Unassigned / Central --</option>
                {farms.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.farm_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Quantity, Unit Measurement, Unit Price */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Quantity *</label>
              <Input
                type="number"
                step="any"
                inputMode="decimal"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="e.g. 5"
                className="h-9 text-xs font-mono"
                required
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Unit Type</label>
              <Input
                type="text"
                value={unitMeasurement}
                onChange={(e) => setUnitMeasurement(e.target.value)}
                placeholder="vial / bottle / box"
                className="h-9 text-xs"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Unit Price (₱) *</label>
              <Input
                type="number"
                step="0.01"
                inputMode="decimal"
                value={unitPrice}
                onChange={(e) => setUnitPrice(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="₱0.00"
                className="h-9 text-xs font-mono"
                required
              />
            </div>
          </div>

          {/* Invoice # & Notes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Invoice / Receipt #</label>
              <Input
                type="text"
                value={invoiceNo}
                onChange={(e) => setInvoiceNo(e.target.value)}
                placeholder="e.g. INV-3391"
                className="h-9 text-xs font-mono"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Notes / Remarks</label>
              <Input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. 100 doses per bottle"
                className="h-9 text-xs"
              />
            </div>
          </div>

          {/* Invoice Attachment */}
          <div className="space-y-1.5 p-3 rounded-lg border border-slate-200 bg-slate-50">
            <label className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
              <Paperclip className="w-3.5 h-3.5 text-slate-500" />
              Supplier Medicine Invoice / Receipt (WP07 Secure Storage)
            </label>
            <div className="flex items-center gap-2">
              <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-xs font-medium text-slate-700 shadow-xs">
                <Camera className="w-3.5 h-3.5 text-slate-600" />
                <span>{attachedFile ? 'Change Invoice' : 'Take Photo / Upload Invoice'}</span>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  capture="environment"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>
              {attachedFile && (
                <span className="text-xs font-mono text-emerald-700 truncate max-w-[200px]">
                  ✓ {attachedFile.name}
                </span>
              )}
            </div>
          </div>

          {/* Total Amount Callout */}
          <div className="p-3 rounded-lg bg-slate-900 text-white flex items-center justify-between font-mono">
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Total Booked Amount</p>
              <p className="text-xs text-slate-300">
                {quantity || 0} {unitMeasurement || 'units'}
                {selectedCategory ? ` • [${selectedCategory.code}] ${selectedCategory.name}` : ''}
              </p>
            </div>
            <div className="text-right">
              <span className="text-base sm:text-lg font-bold text-emerald-400">
                ₱{totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>

          <DialogFooter className="pt-2 border-t border-slate-200 flex flex-row items-center justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={loading}
              className="bg-teal-700 hover:bg-teal-800 text-white font-semibold"
            >
              {loading ? 'Saving Receipt...' : 'Save Medicine Receipt'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
